import type { SelectionProcessStep, StepPlanCoverageEntry } from "@lib/selectionSteps";

// Looks up a current-matrix dimension name by continuityId. Returns null when
// the continuity is no longer in the current matrix — the diff formatter then
// falls back to the row's own `dimensionName` (last-known name from the
// continuity chain) and annotates the line as historical.
export type DimensionNameResolver = (continuityId: string) => string | null;

const DEPTH_LABELS: Record<string, string> = {
  sample: "Screen",
  probe: "Probe",
  deep_dive: "Deep dive",
};

const formatDepth = (depth: string): string => DEPTH_LABELS[depth] ?? depth;

const formatCoverageEntry = (
  entry: StepPlanCoverageEntry,
  resolve: DimensionNameResolver,
): string => {
  const current = resolve(entry.dimensionId);
  if (current) return `${current} · ${formatDepth(entry.depth)}`;
  const historical = entry.dimensionName?.trim();
  if (historical) return `${historical} (no longer in matrix) · ${formatDepth(entry.depth)}`;
  return `(unknown dimension) · ${formatDepth(entry.depth)}`;
};

export interface FieldChange {
  label: string;
  oldValue: string;
  newValue: string;
}

export interface AddedRow {
  stepType: string;
  label: string | null;
}

export interface RemovedRow {
  continuityId: string;
  label: string | null;
  stepType: string;
  inFlightCandidateCount: number;
}

export interface ReorderedRow {
  continuityId: string;
  label: string | null;
  stepType: string;
  fromIndex: number;
  toIndex: number;
}

export interface EditedRow {
  continuityId: string;
  label: string | null;
  stepType: string;
  fieldChanges: FieldChange[];
}

export interface SelectionStepsDiff {
  added: AddedRow[];
  removed: RemovedRow[];
  reordered: ReorderedRow[];
  edited: EditedRow[];
}

interface ComparableField {
  label: string;
  read: (step: SelectionProcessStep) => string;
}

const formatScalar = (value: unknown): string => {
  if (value == null || value === "") return "";
  return String(value);
};

const formatParticipants = (value: unknown): string => {
  if (!Array.isArray(value) || value.length === 0) return "";
  const lines = value
    .map((p) => {
      const role = String(p?.role ?? "");
      const name = String(p?.name ?? "");
      const membership = String(p?.organisationMembershipId ?? "");
      return `${role}|${name}|${membership}`;
    })
    .sort();
  return lines.join("\n");
};

const formatCoverage = (value: unknown, resolve: DimensionNameResolver): string => {
  if (!Array.isArray(value) || value.length === 0) return "";
  return value
    .map((c) => formatCoverageEntry(c as StepPlanCoverageEntry, resolve))
    .sort()
    .join("\n");
};

const COMPARABLE_FIELD_LABELS = {
  STEP_TYPE: "Step type",
  LABEL: "Label",
  FORMAT: "Format",
  DURATION: "Duration (minutes)",
  PURPOSE: "Purpose",
  PARTICIPANTS: "Participants",
  ASSESSMENT_INTENT: "Assessment intent",
  COVERAGE: "Coverage",
} as const;

const comparableFields = (resolve: DimensionNameResolver): ComparableField[] => [
  { label: COMPARABLE_FIELD_LABELS.STEP_TYPE, read: (s) => formatScalar(s.stepType) },
  { label: COMPARABLE_FIELD_LABELS.LABEL, read: (s) => formatScalar(s.label) },
  { label: COMPARABLE_FIELD_LABELS.FORMAT, read: (s) => formatScalar(s.stepFormat) },
  { label: COMPARABLE_FIELD_LABELS.DURATION, read: (s) => formatScalar(s.durationMinutes) },
  { label: COMPARABLE_FIELD_LABELS.PURPOSE, read: (s) => formatScalar(s.purpose) },
  { label: COMPARABLE_FIELD_LABELS.PARTICIPANTS, read: (s) => formatParticipants(s.participants) },
  {
    label: COMPARABLE_FIELD_LABELS.ASSESSMENT_INTENT,
    read: (s) => formatScalar(s.assessmentPlan?.intent),
  },
  {
    label: COMPARABLE_FIELD_LABELS.COVERAGE,
    read: (s) => formatCoverage(s.assessmentPlan?.coverage, resolve),
  },
];

const noopResolver: DimensionNameResolver = () => null;

export function computeDiff(
  initial: SelectionProcessStep[],
  current: SelectionProcessStep[],
  resolveDimensionName: DimensionNameResolver = noopResolver,
): SelectionStepsDiff {
  const initialById = new Map<string, SelectionProcessStep>();
  for (const step of initial) {
    if (step.continuityId) initialById.set(step.continuityId, step);
  }

  const currentById = new Map<string, SelectionProcessStep>();
  for (const step of current) {
    if (step.continuityId) currentById.set(step.continuityId, step);
  }

  const added: AddedRow[] = current
    .filter((s) => !s.continuityId)
    .map((s) => ({ stepType: s.stepType, label: s.label ?? null }));

  const removed: RemovedRow[] = initial
    .filter((s) => s.continuityId && !currentById.has(s.continuityId))
    .map((s) => ({
      continuityId: s.continuityId as string,
      label: s.label ?? null,
      stepType: s.stepType,
      inFlightCandidateCount: s.inFlightCandidateCount ?? 0,
    }));

  const sharedInitialOrder = initial
    .filter((s) => s.continuityId && currentById.has(s.continuityId))
    .map((s) => s.continuityId as string);
  const sharedCurrentOrder = current
    .filter((s) => s.continuityId && initialById.has(s.continuityId))
    .map((s) => s.continuityId as string);

  const initialIndex = new Map<string, number>();
  for (let i = 0; i < sharedInitialOrder.length; i++) {
    initialIndex.set(sharedInitialOrder[i], i);
  }
  const currentIndex = new Map<string, number>();
  for (let i = 0; i < sharedCurrentOrder.length; i++) {
    currentIndex.set(sharedCurrentOrder[i], i);
  }

  const reordered: ReorderedRow[] = [];
  for (const id of sharedCurrentOrder) {
    const from = initialIndex.get(id);
    const to = currentIndex.get(id);
    if (from === undefined || to === undefined) continue;
    if (from === to) continue;
    const cur = currentById.get(id);
    if (!cur) continue;
    reordered.push({
      continuityId: id,
      label: cur.label ?? null,
      stepType: cur.stepType,
      fromIndex: from,
      toIndex: to,
    });
  }

  const edited: EditedRow[] = [];
  const fields = comparableFields(resolveDimensionName);
  for (const id of sharedCurrentOrder) {
    const before = initialById.get(id);
    const after = currentById.get(id);
    if (!before || !after) continue;
    const fieldChanges: FieldChange[] = [];
    for (const field of fields) {
      const oldValue = field.read(before);
      const newValue = field.read(after);
      if (oldValue !== newValue) {
        fieldChanges.push({ label: field.label, oldValue, newValue });
      }
    }
    if (fieldChanges.length > 0) {
      edited.push({
        continuityId: id,
        label: after.label ?? null,
        stepType: after.stepType,
        fieldChanges,
      });
    }
  }

  return { added, removed, reordered, edited };
}
